package com.example.socialmediaapp.network

import com.example.socialmediaapp.data.models.Post
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ============================================
// API DTOs (snake_case from server)
// ============================================

@Serializable
data class ApiPost(
    val id: String,
    @SerialName("author_id") val authorId: String,
    @SerialName("author_name") val authorName: String,
    @SerialName("author_avatar") val authorAvatar: String? = null,
    val content: String,
    @SerialName("image_urls") val imageUrls: List<String> = emptyList(),
    val timestamp: Long,
    @SerialName("like_count") val likeCount: Int = 0,
    @SerialName("comment_count") val commentCount: Int = 0,
    @SerialName("share_count") val shareCount: Int = 0,
    @SerialName("is_liked_by_me") val isLikedByMe: Boolean = false,
    @SerialName("media_type") val mediaType: String = "text",
    @SerialName("video_url") val videoUrl: String? = null,
    @SerialName("voice_url") val voiceUrl: String? = null,
    @SerialName("media_duration") val mediaDuration: Int? = null
) {
    fun toDomain() = Post(
        id = id,
        authorId = authorId,
        authorName = authorName,
        authorAvatar = authorAvatar,
        content = content,
        imageUrls = imageUrls,
        timestamp = timestamp * 1000L,
        likeCount = likeCount,
        commentCount = commentCount,
        shareCount = shareCount,
        isLikedByMe = isLikedByMe
    )
}

@Serializable
data class CreatePostRequest(
    val content: String,
    val imageUrls: List<String> = emptyList(),
    val videoUrl: String? = null,
    val voiceUrl: String? = null,
    val mediaType: String = "text",
    val mediaDuration: Int? = null
)

@Serializable
data class CreatePostResponse(val id: String = "")

// ============================================
// FEED REPOSITORY
// ============================================

class FeedRepository(private val api: ApiClient) {

    suspend fun getFeed(): Result<List<Post>> = runCatching {
        api.get<List<ApiPost>>("/api/feed").map { it.toDomain() }
    }

    suspend fun createPost(
        content: String,
        imageUrls: List<String> = emptyList(),
        videoUrl: String? = null,
        voiceUrl: String? = null,
        mediaType: String = "text",
        mediaDuration: Int? = null,
        effectType: String? = null
    ): Result<String> = runCatching {
        val endpoint = when (effectType) {
            "glitch" -> "/api/effects/glitch"
            "blur" -> "/api/effects/blur"
            "vintage" -> "/api/effects/vintage"
            "video-glitch" -> "/api/effects/video-glitch"
            else -> "/api/posts"
        }
        
        // In a real upload, we'd use multipart here for the effects, 
        // but for this logic flow, we'll route the request accordingly.
        val response = api.post<CreatePostResponse>(
            endpoint,
            CreatePostRequest(content, imageUrls, videoUrl, voiceUrl, mediaType, mediaDuration)
        )
        response.id
    }
}
